import { useCallback } from 'react';

import { EikaMessageEvent, parseCallback, useNativeEventListener } from '@eika-infrastruktur/mobilbank-lib';

import type { ApprovalStatus } from '../generated-types';
import { useSigningContext } from './signingContext';
import { Actions } from './signingTypes';

const getPaymentStatus = (payload?: string): ApprovalStatus => {
    if (!payload) return 'UNKNOWN';
    try {
        return payload as ApprovalStatus;
    } catch (_) {
        return 'UNKNOWN';
    }
};

type SigningWebActions =
    | 'BROWSER_CALLBACK_RESULT'
    | 'SIGNING_STATUS_UPDATE_PAYMENT'
    | 'SIGNING_CANCEL'
    | 'PAYMENT_CANCEL'
    | 'SIGNING_ERROR';

const useSigningContextResult = (closePayment: () => void) => {
    const { dispatch, showSigningFlow } = useSigningContext();

    const onMessage = useCallback(
        (event: EikaMessageEvent<SigningWebActions>) => {
            if (!showSigningFlow) return;
            const data = parseCallback<SigningWebActions>(event.data);
            if (data) {
                switch (data.action) {
                    case 'BROWSER_CALLBACK_RESULT': {
                        switch (data.payload) {
                            case 'CANCEL': {
                                dispatch({ type: Actions.CANCEL });
                                break;
                            }
                            case 'ERROR': {
                                dispatch({ type: Actions.COMPLETE_SIGNING, status: 'REJECTED' });
                                break;
                            }
                            default: {
                                dispatch({
                                    type: Actions.COMPLETE_SIGNING,
                                    status: getPaymentStatus(data.payload as string),
                                });
                                break;
                            }
                        }
                        break;
                    }
                    case 'SIGNING_STATUS_UPDATE_PAYMENT': {
                        dispatch({ type: Actions.COMPLETE_SIGNING, status: getPaymentStatus(data.payload as string) });
                        break;
                    }
                    case 'SIGNING_CANCEL': {
                        dispatch({ type: Actions.CANCEL });
                        break;
                    }
                    case 'PAYMENT_CANCEL': {
                        dispatch({ type: Actions.RESET });
                        closePayment();
                        break;
                    }
                    case 'SIGNING_ERROR': {
                        if (data.payload === 'DYNAMIC_LINK_EXPIRED') {
                            dispatch({ type: Actions.COMPLETE_SIGNING, status: 'EXPIRED' });
                        } else {
                            dispatch({ type: Actions.COMPLETE_SIGNING, status: 'REJECTED' });
                        }
                        break;
                    }
                    default:
                        break;
                }
            }
        },
        [closePayment, dispatch, showSigningFlow],
    );
    useNativeEventListener(onMessage);
    return { cancelSigning: () => dispatch({ type: Actions.CANCEL_SIGNING }) };
};

export default useSigningContextResult;
